import { Currency } from "@redotech/money/currencies";
import { getCurrencyFormatter } from "@redotech/money/util";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import { SlideInPanelAnchorLocation } from "../slide-in-panel/slide-in-panel-anchor-location";

const baseDiscountSchema = z.object({
  autoGenerateRandomCode: z.boolean().optional(),
  title: z.string().optional(),
});

const fixedAmountDiscountSchema = baseDiscountSchema.extend({
  discountType: z.literal("fixed_amount"),
  amount: z.object({
    currencyCode: createEnum(Currency, { enumName: "Currency" }),
    value: z.string(),
  }),
});

const percentageDiscountSchema = baseDiscountSchema.extend({
  discountType: z.literal("percentage"),
  percentage: z.string(),
});

export const rescueModalDiscountSchema = z.union([
  fixedAmountDiscountSchema,
  percentageDiscountSchema,
]);
export type RescueModalDiscount = z.infer<typeof rescueModalDiscountSchema>;

export const RESCUE_MODAL_UI_BUTTON_SUBTEXT_DEFAULT = {
  shouldShow: "if_coverage_eligible" as const,
  value: "(Checkout+ included)",
};
export function getDefaultRescueModalTitle(
  discount: RescueModalDiscount | undefined,
): string {
  function formatDiscount(discount: RescueModalDiscount): string {
    if (discount.discountType === "percentage") {
      return `${discount.percentage}%`;
    }
    const formatter = getCurrencyFormatter(discount.amount.currencyCode);
    return formatter.format(parseFloat(discount.amount.value));
  }

  if (!discount) {
    return "Your discount is waiting";
  }
  return `Your ${formatDiscount(discount)} discount is waiting`;
}
export const DEFAULT_RESCUE_MODAL_DESCRIPTION =
  "Go to checkout with the button below to complete your purchase and save on this order.";
export const DEFAULT_RESCUE_MODAL_BUTTON_TEXT = "Apply discount";
export const DEFAULT_RESCUE_MODAL_PLACEMENT =
  SlideInPanelAnchorLocation.TOP_LEFT;

export const rescueModalUiButtonSubtextSchema = z.union([
  z.object({
    shouldShow: z.union([
      z.literal("always"),
      z.literal("if_coverage_eligible"),
    ]),
    value: z.string().optional(),
  }),
  z.object({
    shouldShow: z.literal("never"),
  }),
]);
export type RescueModalUiButtonSubtext = z.infer<
  typeof rescueModalUiButtonSubtextSchema
>;

export const rescueModalUiSchema = z.object({
  button: z
    .object({
      text: z.string().optional(),
      subtext: rescueModalUiButtonSubtextSchema.optional(),
    })
    .optional(),
  title: z.string().optional(),
  description: z.string().optional(),
  placement: createEnum(SlideInPanelAnchorLocation, {
    enumName: "SlideInPanelAnchorLocation",
  }).optional(),
});
export type RescueModalUi = z.infer<typeof rescueModalUiSchema>;

export const DEFAULT_DROPOFF_PERCENTAGE_TRIGGER = 50;

export const rescueModalSchema = z.object({
  enabled: z.boolean(),
  discountCode: z.string().optional(),
  discount: rescueModalDiscountSchema.optional(),
  dropoffPercentageTrigger: z.number().min(0).max(100).optional(),
  secondsInColdTrigger: z.number().optional(),
  ui: rescueModalUiSchema.optional(),
});
export type RescueModal = z.infer<typeof rescueModalSchema>;

export const rescueModalDefault: RescueModal = {
  enabled: false,
};

export const activeCheckoutHazardModelSchema = z.object({
  endpointName: z.string(),
  modelVersion: z.enum(["v-08-11-25", "v-11-25-25"]).default("v-08-11-25"),
});
export type ActiveCheckoutHazardModel = z.infer<
  typeof activeCheckoutHazardModelSchema
>;

export const predictiveConversionSettingsSchema = z.object({
  enabled: z.boolean(),
  activeCheckoutHazardModel: activeCheckoutHazardModelSchema.optional(),
  rescueModal: rescueModalSchema.optional(),
});
export type PredictiveConversionSettings = z.infer<
  typeof predictiveConversionSettingsSchema
>;

export const predictiveConversionSettingsDefault: PredictiveConversionSettings =
  { enabled: false };
